// frontend/src/pages/devioz/Roles.tsx
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { listRoles, createRole, deleteRole, type Role, type RoleScope } from "../../services/roles";

// ✅ si tu backend solo soporta "GLOBAL" | "COMPANY" entonces deja solo esos 2.
// Aquí lo dejo compatible con tu UI y sin reventar.
type AllowedRoleCode = "DEVIOZ_GERENTE" | "DEVIOZ_ADMIN" | "DEVIOZ_SOCIO" | "DEVIOZ_PROGRAMADOR";

const CODE_OPTIONS: { value: AllowedRoleCode; label: string }[] = [
  { value: "DEVIOZ_GERENTE", label: "DEVIOZ_GERENTE" },
  { value: "DEVIOZ_ADMIN", label: "DEVIOZ_ADMIN" },
  { value: "DEVIOZ_SOCIO", label: "DEVIOZ_SOCIO" },
  { value: "DEVIOZ_PROGRAMADOR", label: "DEVIOZ_PROGRAMADOR" },
];

// ⚠️ IMPORTANTE:
// En tu service RoleScope era "GLOBAL" | "COMPANY".
// Tu UI antes estaba usando "DEVIOZ", "BCP", etc. (eso rompe tipos y suele romper backend).
// Para eliminar 404, lo normal es que el backend espere "GLOBAL"/"COMPANY".
const SCOPE_OPTIONS: { value: RoleScope; label: string }[] = [
  { value: "GLOBAL", label: "Global" },
  { value: "COMPANY", label: "Company" },
];

export default function Roles() {
  const [rows, setRows] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [code, setCode] = useState<AllowedRoleCode>("DEVIOZ_GERENTE");
  const [name, setName] = useState<string>("Gerente Devioz");
  const [scope, setScope] = useState<RoleScope>("GLOBAL");

  const scopeLabelMap = useMemo(() => {
    return SCOPE_OPTIONS.reduce<Record<string, string>>((acc, s) => {
      acc[s.value] = s.label;
      return acc;
    }, {});
  }, []);

  useEffect(() => {
    document.title = "Roles • Devioz";
  }, []);

  async function refresh() {
    setLoading(true);
    setErr(null);
    try {
      const res = await listRoles({ page: 1, pageSize: 200 });

      // listRoles puede devolver array o paginado. Aquí normalizamos.
      const data = Array.isArray(res) ? res : (res as any)?.data ?? [];
      setRows(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setErr(e?.message || "Error cargando roles.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  function onChangeCode(newCode: AllowedRoleCode) {
    setCode(newCode);
    switch (newCode) {
      case "DEVIOZ_GERENTE":
        setName("Gerente Devioz");
        break;
      case "DEVIOZ_ADMIN":
        setName("Administrador Devioz");
        break;
      case "DEVIOZ_SOCIO":
        setName("Socio Devioz");
        break;
      case "DEVIOZ_PROGRAMADOR":
        setName("Programador Devioz");
        break;
      default:
        setName("");
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setMsg(null);

    const nameTrim = name.trim();
    if (!nameTrim) {
      setErr("El nombre es obligatorio.");
      return;
    }

    try {
      setSaving(true);

      const created = await createRole({
        code,
        name: nameTrim,
        scope,
      });

      setMsg(`Rol "${created.name}" creado correctamente.`);
      await refresh();
    } catch (e: any) {
      setErr(e?.message || "Error creando rol.");
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(r: Role) {
    if (!window.confirm(`¿Eliminar el rol "${r.code}"?`)) return;

    setErr(null);
    setMsg(null);

    try {
      await deleteRole(r.id);
      setMsg(`Rol "${r.code}" eliminado correctamente.`);
      await refresh();
    } catch (e: any) {
      setErr(e?.message || "Error eliminando rol.");
    }
  }

  function getScopeLabel(r: Role): string {
    const raw = (r.scope as string | undefined) ?? "";
    if (!raw) return "Global";
    return scopeLabelMap[raw] ?? raw;
  }

  return (
    <div>
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item">
          <Link to="/devioz">Home</Link>
        </li>
        <li className="breadcrumb-item">Devioz</li>
        <li className="breadcrumb-item active">Roles</li>
      </ol>

      <h1 className="page-header mb-3">Roles (Devioz)</h1>

      {(err || msg) && (
        <div className={`alert ${err ? "alert-danger" : "alert-success"}`}>
          {err || msg}
        </div>
      )}

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">Nuevo rol</h5>

          <form className="row gy-2 gx-3" onSubmit={onSubmit} noValidate>
            <div className="col-md-3">
              <label className="form-label">Código *</label>
              <select
                className="form-select"
                value={code}
                onChange={(e) => onChangeCode(e.target.value as AllowedRoleCode)}
                disabled={saving}
              >
                {CODE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Nombre *</label>
              <input
                className="form-control"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Gerente Devioz"
                disabled={saving}
                required
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Scope *</label>
              <select
                className="form-select"
                value={scope}
                onChange={(e) => setScope(e.target.value as RoleScope)}
                disabled={saving}
              >
                {SCOPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3 d-flex align-items-end">
              <button className="btn btn-primary w-100" disabled={saving}>
                {saving ? "Guardando…" : "Crear rol"}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <h5 className="card-title mb-3">Listado de roles</h5>

          {loading ? (
            <div>Cargando...</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped align-middle">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Código</th>
                    <th>Nombre</th>
                    <th>Scope</th>
                    <th>Creado</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length ? (
                    rows.map((r, idx) => {
                      const createdAt = r.created_at as string | undefined;
                      return (
                        <tr key={r.id}>
                          <td>{idx + 1}</td>
                          <td>{r.code}</td>
                          <td>{r.name}</td>
                          <td>{getScopeLabel(r)}</td>
                          <td>{createdAt ? new Date(createdAt).toLocaleString() : "-"}</td>
                          <td className="text-center">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => onDelete(r)}
                              title="Eliminar rol"
                            >
                              🗑
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center">
                        Sin roles definidos
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
